import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DeviceTrackingService } from '../shared/services/device-tracking.service';

/**
 * Adds `x-device-id`, `x-device-type` and `x-browser` to every request for our API, logged in or
 * not, plus `x-country` once the user has logged in, so the backend's request log knows which
 * browser and country sent it (see DeviceTrackingService).
 *
 * Independent of AuthInterceptor: it neither reads nor changes the token, and never looks at a
 * response, so the 401 session-expiry path is untouched.
 */
@Injectable()
export class DeviceInfoInterceptor implements HttpInterceptor {
  constructor(private readonly deviceTracking: DeviceTrackingService) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(this.withDeviceHeaders(request));
  }

  /**
   * Only `API_BASE_URL` gets the headers: a third-party host (ipapi.co, Coinsbit, the
   * landing-page CMS) has not allowed them and would fail the request's CORS preflight.
   * Any error here sends the request exactly as it was: the log is never worth a failed call.
   */
  private withDeviceHeaders(request: HttpRequest<unknown>): HttpRequest<unknown> {
    try {
      if (!environment.DEVICE_TRACKING_ENABLED || !request.url.startsWith(environment.API_BASE_URL)) {
        return request;
      }
      const headers = this.deviceTracking.getHeaders();
      return Object.keys(headers).length ? request.clone({ setHeaders: headers }) : request;
    } catch (error) {
      console.error('Device headers skipped:', error);
      return request;
    }
  }
}
